import { API_USE_MOCK } from "./config";
import { defaultStartPath } from "../constants/defaultValues";
import entities from "../data/suiteEntities.json";

type EntityWithId = { id: unknown };

const delay = (ms = 160) => new Promise<void>(r => setTimeout(r, ms));

async function mock<T = unknown>(data?: T | (() => T)) {
  if (!API_USE_MOCK) {
    throw new Error(
      "API_USE_MOCK is false — wire Api/suiteAdapters to your backend."
    );
  }
  await delay();
  if (data === undefined) return undefined as T;
  return typeof data === "function" ? (data as () => T)() : data;
}

function byId<T extends EntityWithId>(list: T[], id: unknown): T | null {
  return list.find(x => String(x.id) === String(id)) ?? null;
}

/** @param {Record<string, string>} sla */
let slaStore = {
  firstResponseMins: "60",
  resolutionMins: "480",
  businessHours: "09:00–18:00 ET"
};

function countBy<T>(
  arr: T[] | null | undefined,
  keyFn: (row: T) => string
): Record<string, number> {
  return (arr ?? []).reduce<Record<string, number>>((acc, row) => {
    const k = keyFn(row);
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
}

export const suiteApi = {
  ticketing: {
    listTickets: () => mock(entities.ticketingTickets),
    getQueueSummary: () =>
      mock(() => {
        const tickets = entities.ticketingTickets;
        return {
          total: tickets.length,
          open: tickets.filter(t => t.status === "open").length,
          atRisk: tickets.filter(t => t.slaStatus === "at_risk").length,
          byQueue: countBy(tickets, t => t.queue || "General")
        };
      }),
    getTicket: (id: unknown) =>
      mock(() => {
        const t = byId(entities.ticketingTickets, id);
        if (!t) return null;
        return {
          ...t,
          description:
            "Full thread and metadata would load from your helpdesk API.",
          thread: [
            { at: t.updatedAt, author: t.requester, body: "Initial request logged." },
            { at: "2026-03-28T12:00:00Z", author: t.assignee, body: "Investigating — will update today." }
          ]
        };
      }),
    listCustomers: () => mock(entities.ticketingCustomers),
    getCustomer: (id: unknown) => mock(() => byId(entities.ticketingCustomers, id)),
    listMacros: () => mock(entities.ticketingMacros),
    getSla: () => mock(() => ({ ...slaStore })),
    saveSla: async (values: Record<string, string>) => {
      await mock();
      slaStore = { ...slaStore, ...values };
    }
  },
  main: {
    listNotifications: () => mock(entities.mainNotifications),
    search: (q: string | undefined) =>
      mock(() => {
        const term = (q || "").toLowerCase();
        if (!term) return entities.mainSearchHits;
        return entities.mainSearchHits.filter(
          h =>
            h.title.toLowerCase().includes(term) ||
            h.path.toLowerCase().includes(term)
        );
      }),
    getShortcuts: () =>
      mock(() => ({
        links: [
          {
            labelId: "suite.main.shortcut-finance",
            to: `${defaultStartPath}/finance`,
            subtitleId: "suite.main.shortcut-finance-sub"
          },
          {
            labelId: "suite.main.shortcut-tickets",
            to: `${defaultStartPath}/ticketing/tickets`,
            subtitleId: "suite.main.shortcut-tickets-sub"
          }
        ]
      }))
  },
  blogging: {
    listPosts: () => mock(entities.blogPosts),
    getPost: (id: unknown) => mock(() => byId(entities.blogPosts, id)),
    listCategories: () => mock(entities.blogCategories),
    listMedia: () => mock(entities.blogMedia),
    listComments: () => mock(entities.blogComments)
  },
  storage: {
    listFiles: () => mock(entities.storageFiles),
    getFile: (id: unknown) => mock(() => byId(entities.storageFiles, id)),
    listShares: () => mock(entities.storageShares),
    getStorageSettings: () =>
      mock(() => ({ quotaGb: "500", retentionDays: "365" })),
    saveStorageSettings: (v: Record<string, unknown>) =>
      mock(() => Object.assign({}, v))
  },
  adverts: {
    listCampaigns: () => mock(entities.adCampaigns),
    getCampaign: (id: unknown) => mock(() => byId(entities.adCampaigns, id)),
    getReportSummary: () =>
      mock(() => ({
        impressions: 1284000,
        clicks: 42000,
        spend: 20600,
        roas: 3.2
      })),
    getBilling: () =>
      mock(() => ({ balance: 1240, nextInvoice: "2026-04-01", paymentMethod: "Visa •••• 4242" }))
  },
  crm: {
    listDeals: () => mock(entities.crmDeals),
    getDeal: (id: unknown) => mock(() => byId(entities.crmDeals, id)),
    listAccounts: () => mock(entities.crmAccounts),
    getAccount: (id: unknown) => mock(() => byId(entities.crmAccounts, id)),
    listContacts: () => mock(entities.crmContacts),
    listActivities: () => mock(entities.crmActivities)
  },
  analytics: {
    listReports: () => mock(entities.analyticsReports),
    getReport: (id: unknown) => mock(() => byId(entities.analyticsReports, id)),
    getBuilderState: () =>
      mock(() => ({ dataset: "events", dimensions: "country,device", metrics: "sessions" })),
    saveBuilderState: (v: Record<string, unknown>) => mock(() => v),
    listExports: () =>
      mock(() => [
        {
          id: "ex_1",
          name: "sessions_mar.csv",
          created: "2026-03-28",
          status: "ready"
        },
        {
          id: "ex_2",
          name: "funnel_q1.json",
          created: "2026-03-20",
          status: "queued"
        },
        {
          id: "ex_3",
          name: "errors_weekly.parquet",
          created: "2026-03-29",
          status: "failed"
        }
      ]),
    retryExport: async (id: string) => {
      await mock();
      return { id, status: "queued" };
    }
  },
  ecommerce: {
    listProducts: () => mock(entities.ecomProducts),
    getProduct: (id: unknown) => mock(() => byId(entities.ecomProducts, id)),
    listOrders: () => mock(entities.ecomOrders),
    getOrder: (id: unknown) => mock(() => byId(entities.ecomOrders, id)),
    listCustomers: () => mock(entities.ecomCustomers),
    getCustomer: (id: unknown) => mock(() => byId(entities.ecomCustomers, id)),
    listReturns: () => mock(entities.ecomReturns)
  },
  finance: {
    listInvoices: () => mock(entities.financeInvoices),
    getInvoice: (id: unknown) => mock(() => byId(entities.financeInvoices, id)),
    listPayments: () => mock(entities.financePayments),
    getPaymentMix: () =>
      mock(() => {
        const pays = entities.financePayments;
        const byMethod = countBy(pays, (p: { method: string }) => p.method);
        return {
          byMethod,
          totalAmount: pays.reduce(
            (s: number, p: { amount: number }) => s + p.amount,
            0
          )
        };
      }),
    listExpenses: () => mock(entities.financeExpenses),
    listBudgets: () => mock(entities.financeBudgets),
    getAgingSummary: () =>
      mock(() => {
        const inv = entities.financeInvoices;
        return {
          current: inv.filter(
            i => i.status === "sent" && (i.agingDays || 0) <= 30
          ).length,
          overdue: inv.filter(
            i => i.status === "overdue" || (i.status !== "paid" && (i.agingDays || 0) > 30)
          ).length,
          paid: inv.filter(i => i.status === "paid").length
        };
      })
  },
  industrial: {
    listAssets: () => mock(entities.industrialAssets),
    getAsset: (id: unknown) => mock(() => byId(entities.industrialAssets, id)),
    listAlerts: () => mock(entities.industrialAlerts),
    listWorkOrders: () => mock(entities.industrialWorkOrders),
    getWorkOrder: (id: unknown) => mock(() => byId(entities.industrialWorkOrders, id))
  },
  aiStudio: {
    listProjects: () => mock(entities.aiProjects),
    getProject: (id: unknown) => mock(() => byId(entities.aiProjects, id)),
    listKeys: () => mock(entities.aiKeys),
    listLogs: () => mock(entities.aiLogs),
    listExperiments: () => mock(entities.aiExperiments),
    getAiSettings: () =>
      mock(() => ({ rpmLimit: "10000", monthlyBudgetUsd: "5000" })),
    saveAiSettings: (v: Record<string, unknown>) => mock(() => v)
  },
  devops: {
    listServices: () => mock(entities.devServices),
    getService: (id: unknown) => mock(() => byId(entities.devServices, id)),
    listIncidents: () => mock(entities.devIncidents),
    getIncident: (id: unknown) => mock(() => byId(entities.devIncidents, id)),
    listDeploys: () => mock(entities.devDeploys),
    listRunbooks: () => mock(entities.devRunbooks),
    getOncall: () =>
      mock(() => ({ primary: "Jordan Lee", secondary: "Sam Rivera", rotation: "Week 13" })),
    saveOncall: (v: Record<string, unknown>) => mock(() => v)
  },
  plg: {
    listAccounts: () => mock(entities.plgAccounts),
    getAccount: (id: unknown) => mock(() => byId(entities.plgAccounts, id)),
    listTrials: () => mock(entities.plgTrials),
    getTrialFunnel: () =>
      mock(() => {
        const tr = entities.plgTrials;
        return {
          signup: tr.filter(t => t.step === "Signup").length,
          activation: tr.filter(t => t.step === "Activation").length,
          convert: tr.filter(t => t.step === "Paid convert").length
        };
      }),
    listFeatures: () => mock(entities.plgFeatures),
    listChurn: () => mock(entities.plgChurn)
  },
  hr: {
    listEmployees: () => mock(entities.hrEmployees),
    getEmployee: (id: unknown) => mock(() => byId(entities.hrEmployees, id)),
    listRequisitions: () => mock(entities.hrRequisitions),
    getRequisition: (id: unknown) => mock(() => byId(entities.hrRequisitions, id)),
    listTimeoff: () => mock(entities.hrTimeoff),
    getHrSettings: () =>
      mock(() => ({ defaultLocation: "Remote", workWeek: "Mon–Fri" })),
    saveHrSettings: (v: Record<string, unknown>) => mock(() => v)
  },
  logistics: {
    listShipments: () => mock(entities.logShipments),
    getShipment: (id: unknown) => mock(() => byId(entities.logShipments, id)),
    listCarriers: () => mock(entities.logCarriers),
    listWarehouses: () => mock(entities.logWarehouses),
    listExceptions: () => mock(entities.logExceptions)
  },
  healthcare: {
    listAppointments: () => mock(entities.healthAppointments),
    getAppointment: (id: unknown) => mock(() => byId(entities.healthAppointments, id)),
    listClaims: () => mock(entities.healthClaims),
    getClaim: (id: unknown) => mock(() => byId(entities.healthClaims, id)),
    listProviders: () => mock(entities.healthProviders)
  },
  education: {
    listCourses: () => mock(entities.eduCourses),
    getCourse: (id: unknown) => mock(() => byId(entities.eduCourses, id)),
    listCohorts: () => mock(entities.eduCohorts),
    getCohort: (id: unknown) => mock(() => byId(entities.eduCohorts, id)),
    listLearners: () => mock(entities.eduLearners),
    getLearner: (id: unknown) => mock(() => byId(entities.eduLearners, id))
  },
  realEstate: {
    listListings: () => mock(entities.reListings),
    getListing: (id: unknown) => mock(() => byId(entities.reListings, id)),
    listLeads: () => mock(entities.reLeads),
    listTransactions: () => mock(entities.reTransactions)
  },
  security: {
    listVulns: () => mock(entities.secVulns),
    getVuln: (id: unknown) =>
      mock(() => {
        const v = byId(entities.secVulns, id);
        if (!v) return null;
        return {
          ...v,
          evidence: "Scanner output and patch links would render here.",
          tasks: ["Validate patch in staging", "Schedule change window"]
        };
      }),
    listPolicies: () => mock(entities.secPolicies),
    listAudit: () => mock(entities.secAudit)
  }
};
